import * as React from 'react';
import { CoreInput } from '../internal/WixUiCoreComponents/components/CoreInput';
import { st, classes } from './TextField.st.css';
import {
  Check,
  Error,
  ChevronUpSmall,
  ChevronDownSmall,
} from '@wix/wix-ui-icons-common/on-stage';
import { ClearIcon } from './icons/ClearIcon';
import { Hidden, Visible } from '@wix/wix-ui-icons-common';
import { IconButton, IconButtonSkins, IconButtonThemes } from '../IconButton';
import { Tooltip, TooltipSkin } from '../Tooltip';
import { EMPTY, ERROR, SUCCESS, THEME, DATA_HOOKS } from './dataKeys';
import { IconTooltip } from '../IconTooltip';
import { ErrorMessageWrapper } from '../internal/ErrorMessageWrapper';
import {
  TextFieldProps,
  TextFieldDefaultProps,
  TextFieldState,
  TextFieldTheme,
  TextFieldLabelStyle,
} from './types';
import { getAriaDescribedBy } from '../../common/a11y';
import uniqueId from '../../common/uniqueId';
import { Text } from '../Text';

export class TextField extends React.Component<TextFieldProps, TextFieldState> {
  static displayName = 'TextField';
  private readonly _rootRef = React.createRef<HTMLDivElement>();
  private readonly _inputId: string;
  private readonly _errorMessageId: string;
  private readonly _prefixLabelId: string;
  private readonly _isPassword: boolean;
  private inputRef: HTMLInputElement;
  static defaultProps: TextFieldDefaultProps = {
    success: false,
    successIcon: false,
    error: false,
    withClearButton: false,
    disabled: false,
    theme: TextFieldTheme.Box,
    errorTooltipPlacement: 'top-end',
    forceLabel: false,
    labelStyle: TextFieldLabelStyle.Regular,
  };

  state: TextFieldState = {
    isErrorTooltipShown: false,
    isPasswordVisible: this.props.visible ?? false,
    isFocused: false,
  };

  constructor(props: TextFieldProps) {
    super(props);
    this._isPassword = props.type === 'password';
    this._inputId = props.id ?? uniqueId('label-for-id');
    this._errorMessageId = uniqueId('error-message');
    this._prefixLabelId = uniqueId('prefix-label');
  }

  public TextFieldRef = React.createRef<CoreInput>();

  public focus = (options?: FocusOptions) => {
    this.TextFieldRef.current.focus(options);
  };

  public blur = () => {
    this.TextFieldRef.current.blur();
  };

  public showErrorTooltip = () => {
    this._toggleTooltip(true);
  };

  public hideErrorTooltip = () => {
    this._toggleTooltip(false);
  };

  _getClearButton = (shouldAddClearButtonGap: boolean) => {
    const {
      clearButtonAriaLabel,
      clearButtonAriaLabelledby,
      clearButtonTooltipText,
      disabled,
      onClear,
    } = this.props;
    const tooltip = clearButtonTooltipText ?? 'Clear';

    return (
      <div className={classes.clearButtonWrapper}>
        <Tooltip content={tooltip} disabled={disabled}>
          <IconButton
            className={classes.clearButton}
            data-hook={DATA_HOOKS.CLEAR_BUTTON}
            aria-label={clearButtonAriaLabel}
            aria-labelledby={clearButtonAriaLabelledby}
            skin={IconButtonSkins.Line}
            theme={IconButtonThemes.None}
            onClick={onClear ? () => this.props.onClear() : undefined}
            icon={<ClearIcon />}
          />
        </Tooltip>
        {shouldAddClearButtonGap && <div className={classes.clearButtonGap} />}
      </div>
    );
  };

  _getErrorSuffix = () => {
    const { isErrorTooltipShown } = this.state;
    const { errorMessage, errorTooltipPlacement, errorTooltipMaxWidth } =
      this.props;

    return (
      <IconTooltip
        data-hook={DATA_HOOKS.ERROR_ICON}
        message={errorMessage}
        icon={<Error className={classes.errorIcon} />}
        tooltipId={this._errorMessageId}
        tooltipSkin={TooltipSkin.Error}
        showTooltip={isErrorTooltipShown}
        onClick={this._onErrorButtonClick}
        onClickOutside={this._onClickOutside}
        tooltipProps={{
          placement: errorTooltipPlacement,
          moveBy: { x: 5, y: 0 },
          maxWidth: errorTooltipMaxWidth,
        }}
      />
    );
  };

  _getSuccessSuffix = () => {
    return (
      <Check
        data-hook={DATA_HOOKS.SUCCESS_ICON}
        className={classes.successIcon}
      />
    );
  };

  _getArrowsSuffix = () => {
    const triggerInputEvent = () =>
      this.inputRef?.dispatchEvent(new Event('input', { bubbles: true }));
    const stepUp = () => {
      this.inputRef?.stepUp();
      triggerInputEvent();
    };
    const stepDown = () => {
      this.inputRef?.stepDown();
      triggerInputEvent();
    };

    return (
      <div
        className={classes.arrowsSuffixWrapper}
        data-hook={DATA_HOOKS.ARROW_ICONS}
      >
        <button
          className={classes.arrowBtn}
          onClick={stepUp}
          type="button"
          tabIndex={-1}
          aria-hidden={true}
        >
          <ChevronUpSmall size={16} />
        </button>
        <button
          className={classes.arrowBtn}
          onClick={stepDown}
          type="button"
          tabIndex={-1}
          aria-hidden={true}
        >
          <ChevronDownSmall size={16} />
        </button>
      </div>
    );
  };

  _togglePasswordVisibility = () => {
    const { isPasswordVisible } = this.state;
    this.setState({ isPasswordVisible: !isPasswordVisible });
    this.props.onPasswordVisibilityChange?.(
      isPasswordVisible ? 'visible' : 'hidden',
    );
  };

  _handleVisibilityIconPressed = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this._togglePasswordVisibility();
    }
  };

  _getShowHideIcon = () => {
    const { isPasswordVisible } = this.state;
    const { hidePasswordTooltipText, showPasswordTooltipText, disabled } =
      this.props;
    const tooltip = isPasswordVisible
      ? hidePasswordTooltipText ?? 'Hide password'
      : showPasswordTooltipText ?? 'Show password';
    const Icon = isPasswordVisible ? Hidden : Visible;
    const dataHook = isPasswordVisible
      ? DATA_HOOKS.HIDDEN_ICON
      : DATA_HOOKS.VISIBLE_ICON;

    return (
      <div className={classes.customSuffix}>
        <Tooltip content={tooltip} disabled={disabled}>
          <div
            role="button"
            tabIndex={0}
            data-hook={dataHook}
            aria-label={tooltip}
            onClick={!disabled && this._togglePasswordVisibility}
            onKeyDown={!disabled && this._handleVisibilityIconPressed}
            aria-pressed={isPasswordVisible}
          >
            <Icon
              className={st(classes.passwordVisibilityButton, { disabled })}
            />
          </div>
        </Tooltip>
      </div>
    );
  };

  _isNewErrorMessage = () => this.props.newErrorMessage || this._isPassword;

  _getSuffix = () => {
    const {
      error,
      errorMessage,
      success,
      successIcon,
      suffix,
      withClearButton,
      value,
      disabled,
      readOnlyCustom,
      type,
    } = this.props;

    const shouldShowCustomSuffix = !!suffix;
    const shouldShowPasswordSuffix = this._isPassword;
    const shouldShowErrorIcon =
      error && errorMessage && !this._isNewErrorMessage();
    const shouldShowSuccessIcon = successIcon && success;
    const shouldShowClearButton =
      withClearButton && value && !disabled && !readOnlyCustom;
    const shouldShowArrows = type === 'number';

    const shouldAddClearButtonGap =
      shouldShowCustomSuffix || shouldShowErrorIcon || shouldShowSuccessIcon;

    const hasSuffix =
      shouldShowCustomSuffix ||
      shouldShowPasswordSuffix ||
      shouldShowErrorIcon ||
      shouldShowSuccessIcon ||
      shouldShowClearButton ||
      shouldShowArrows;

    return hasSuffix ? (
      <div
        className={st(classes.suffixWrapper, {
          arrows: shouldShowArrows,
        })}
      >
        {shouldShowClearButton
          ? this._getClearButton(shouldAddClearButtonGap)
          : null}
        {shouldShowErrorIcon ? this._getErrorSuffix() : null}
        {shouldShowSuccessIcon ? this._getSuccessSuffix() : null}
        {shouldShowArrows ? this._getArrowsSuffix() : null}
        {shouldShowPasswordSuffix ? this._getShowHideIcon() : null}
        {suffix ? (
          <div
            className={classes.customSuffix}
            data-hook={DATA_HOOKS.CUSTOM_SUFFIX}
          >
            {suffix}
          </div>
        ) : null}
      </div>
    ) : null;
  };

  _getPrefix = () => {
    const { prefix, prefixLabel } = this.props;

    return prefix ? (
      <span data-hook={DATA_HOOKS.PREFIX} className={classes.prefix}>
        {prefix}
        {prefixLabel ? (
          <span
            id={this._prefixLabelId}
            className={classes.prefixLabel}
            data-sr-only="true"
            data-hook={DATA_HOOKS.PREFIX_LABEL}
          >
            {prefixLabel}
          </span>
        ) : null}
      </span>
    ) : null;
  };

  _floatingLabelInput = React.forwardRef<
    HTMLInputElement,
    React.InputHTMLAttributes<HTMLInputElement>
  >((props, ref: React.RefObject<HTMLInputElement>) => {
    const { label, value, theme, placeholder } = this.props;

    const hasFloatingLabelActive = !!value || this.state.isFocused;

    return (
      <div className={classes.floatingInput}>
        <div className={classes.floatingLabelWrapper}>
          <label
            data-hook={DATA_HOOKS.FLOATING_LABEL}
            htmlFor={this._inputId}
            className={st(classes.label, {
              theme,
              style: TextFieldLabelStyle.Floating,
            })}
          >
            {label}
          </label>
          &nbsp;
        </div>
        <input
          {...props}
          ref={ref}
          placeholder={hasFloatingLabelActive ? placeholder : null}
        />
      </div>
    );
  });

  _toggleTooltip = (showTooltip) => {
    this.setState({ isErrorTooltipShown: showTooltip });
  };

  _onErrorButtonClick = () => {
    const { isErrorTooltipShown } = this.state;

    this._toggleTooltip(!isErrorTooltipShown);
  };

  _onClickOutside = () => {
    if (
      this._rootRef.current &&
      !this._rootRef.current.contains(document.activeElement)
    ) {
      this._toggleTooltip(false);
    }
  };

  _onFocus = (e) => {
    const { onFocus } = this.props;

    if (onFocus) {
      onFocus(e);
    }

    if (
      !e.defaultPrevented &&
      this._rootRef.current &&
      this._rootRef.current.contains(document.activeElement)
    ) {
      this._toggleTooltip(true);
    }
    this.setState({ isFocused: true });
  };

  _onBlur = (e) => {
    const { onBlur } = this.props;

    if (onBlur) {
      onBlur(e);
    }
    this.setState({ isFocused: false });
  };

  _getCharCount = () => {
    const { value, maxLength } = this.props;
    return (
      <div data-hook={DATA_HOOKS.CHAR_COUNT} className={classes.charCount}>
        <div dir="ltr">{`${value.length}/${maxLength}`}</div>
      </div>
    );
  };

  render() {
    const {
      errorMessage,
      errorAppearance,
      success,
      successIcon,
      error,
      readOnly,
      dir,
      disabled,
      theme,
      suffix,
      prefix,
      prefixLabel,
      className,
      withClearButton,
      onClear,
      clearButtonAriaLabel,
      clearButtonAriaLabelledby,
      errorTooltipPlacement,
      label,
      labelStyle,
      maxLength,
      showCharCount,
      value,
      forceLabel,
      inputClassName,
      'aria-describedby': ariaDescribedByProp,
      helperText,
      inputRef,
      type,
      readOnlyCustom,
      // Props that should not be passed down to CoreInput.
      newErrorMessage,
      onPasswordVisibilityChange,
      visible,
      lang,

      ...restProps
    } = this.props;
    const dataObject = {
      [THEME]: theme,
      [SUCCESS]: success,
      [ERROR]: error,
      [EMPTY]: !value,
    };

    const Wrapper = this._isNewErrorMessage()
      ? ErrorMessageWrapper
      : React.Fragment;

    const hasPrefix = !!prefix;
    const hasSuffix = !!suffix;
    const hasErrorMessage = !!errorMessage;
    const hasFloatingLabel = labelStyle === TextFieldLabelStyle.Floating;
    const hasFloatingLabelActive =
      hasFloatingLabel && (!!value || this.state.isFocused);

    const stStates = {
      error,
      theme,
      success,
      disabled,
      hasPrefix,
      newErrorMessage: this._isNewErrorMessage(),
      hasSuffix,
      hasErrorMessage,
      hasFloatingLabel,
      hasFloatingLabelActive,
      readOnlyCustom,
    };

    const ariaDescribedBy = getAriaDescribedBy([
      ariaDescribedByProp,
      errorMessage ? this._errorMessageId : '',
      prefixLabel ? this._prefixLabelId : '',
    ]);

    const inputType = this._isPassword
      ? this.state.isPasswordVisible
        ? 'text'
        : 'password'
      : type;

    return (
      <div
        data-hook={DATA_HOOKS.ROOT}
        className={st(classes.root, stStates, className)}
        dir={dir}
        lang={lang}
        ref={this._rootRef}
      >
        <Wrapper
          {...(this._isNewErrorMessage() && {
            visible: error && !!errorMessage,
            message: errorMessage,
            errorAppearance,
            messageId: this._errorMessageId,
            suffixText: showCharCount && maxLength && this._getCharCount(),
            className: st(classes.errorWrapper),
          })}
        >
          {label && !hasFloatingLabel ? (
            <label
              data-hook={DATA_HOOKS.LABEL}
              htmlFor={this._inputId}
              className={st(classes.label, { theme })}
            >
              {label}
              {this.props.required && <span> *</span>}
            </label>
          ) : (
            forceLabel && (
              <div
                className={classes.label}
                aria-hidden="true"
                data-hook={DATA_HOOKS.LABEL_PLACEHOLDER}
              >
                &nbsp;
              </div>
            )
          )}
          <CoreInput
            {...dataObject}
            className={st(
              classes.input,
              { theme },
              helperText && classes.withHelperText,
            )}
            inputClassName={`${inputClassName || ''} has-custom-focus`}
            id={this._inputId}
            aria-describedby={ariaDescribedBy}
            value={value}
            maxLength={maxLength}
            ref={this.TextFieldRef}
            suffix={this._getSuffix()}
            prefix={this._getPrefix()}
            error={error}
            aria-invalid={error}
            {...restProps}
            type={inputType}
            customInput={hasFloatingLabel && this._floatingLabelInput}
            disabled={disabled}
            readOnly={readOnlyCustom ?? readOnly}
            onFocus={this._onFocus}
            onBlur={this._onBlur}
            inputRef={(input) => {
              this.inputRef = input;
              inputRef?.(input);
            }}
          />
          {maxLength &&
            !(this._isNewErrorMessage() && error && errorMessage) &&
            showCharCount &&
            this._getCharCount()}
        </Wrapper>
        {!!helperText && (
          <Text
            className={classes.helperText}
            data-hook={DATA_HOOKS.HELPER_TEXT}
          >
            {helperText}
          </Text>
        )}
      </div>
    );
  }
}
